import {
  analytics_attention_route,
  analytics_overview_route,
  analytics_settings_route,
  VITE_API_URL,
} from "@/constants";
import type { AuthFetchParams } from "./global";
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "@/i18n";
import { authedFetch } from "@/lib/api-client";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";
import { getOrgIdWithFallback } from "@/lib/utils";
import type { AnalyticsAttention, AnalyticsOverview } from "@/lib/types";
import { timeZone, type DateRange } from "@/lib/date-range";

type AnalyticsParams = AuthFetchParams & { range: DateRange };

async function getAnalyticsOverview(params: AnalyticsParams) {
  const query = new URLSearchParams({
    start: params.range.start.toISOString(),
    end: params.range.end.toISOString(),
    tz: timeZone(),
  });
  const res = await fetch(
    `${VITE_API_URL}${analytics_overview_route}?${query}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
        "X-Inventoree-Organization": params.orgId,
      },
      credentials: "include",
    },
  );

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  if (!res.ok) throw new Error(`analytics overview: ${res.status}`);
  return (await res.json()) as AnalyticsOverview;
}

export const analyticsQueryOpts = (params: AnalyticsParams) =>
  queryOptions({
    queryKey: [
      params.orgId,
      "analytics",
      "overview",
      params.range.start.toISOString(),
      params.range.end.toISOString(),
    ],
    queryFn: async () => await getAnalyticsOverview(params),
    staleTime: 60 * 1000,
  });

async function getAnalyticsAttention(params: AuthFetchParams) {
  const res = await fetch(`${VITE_API_URL}${analytics_attention_route}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${params.token}`,
      "X-Inventoree-Organization": params.orgId,
    },
    credentials: "include",
  });

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  if (!res.ok) throw new Error(`analytics attention: ${res.status}`);
  return (await res.json()) as AnalyticsAttention;
}

// what needs attention now, it doesn't depend on the date range
export const attentionQueryOpts = (params: AuthFetchParams) =>
  queryOptions({
    queryKey: [params.orgId, "analytics", "attention"],
    queryFn: async () => await getAnalyticsAttention(params),
    staleTime: 60 * 1000,
    // a big catalog's is worked out in the background, it shows once it is
    refetchInterval: (query) =>
      query.state.data?.updated_at === null ? 60 * 1000 : false,
  });

// Sets whether the organization mostly sells one-off items, the dashboard
// then lists its stale listings instead of what to restock. null works it
// out from the organization's stock.
export function useOneOffItemsMutation() {
  const { getToken, setToken } = useAuth();
  const { orgId } = useOrg();
  const resolvedOrgId = getOrgIdWithFallback(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (oneOffItems: boolean | null) => {
      const res = await authedFetch(analytics_settings_route, {
        method: "POST",
        token: getToken() as string,
        orgId: resolvedOrgId,
        body: JSON.stringify({ one_off_items: oneOffItems }),
      });
      const access = res.headers.get("access-token");
      if (access) setToken(access);
      if (!res.ok) throw new Error(`analytics settings: ${res.status}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [resolvedOrgId, "analytics", "attention"],
      }),
    onError: () => toast.error(i18n.t("dashboard.restock.settingFailed")),
  });
}
